import i18nFactory from '@yandex-int/i18n';
import classNames from 'classnames';
import { TableProps } from 'fixed-data-table-2';
import { CheckBox } from 'lego-on-react';
import { debounce } from 'lodash';
import createReSelector from 're-reselect';
import * as React from 'react';
import { ReactNode, RefObject } from 'react';
import { createSelector } from 'reselect';

import { GridSkeletonContainer } from '../../containers/SkeletonContainer/SkeletonContainer';
import { Bem } from '../../service/Bem/Bem';
import { preventMacOsNavigationGesture } from '../../service/Dom/Dom';
import { GridSourceHelper } from '../../service/GridSourceHelper';
import { maxHeightHelper } from '../../service/MaxHeightHelper';
import { getEmptyRowDataIndexByCurrentScrollPosition } from '../../service/TableHelpers/TableHelpers';
import { TestBem } from '../../service/TestBem';
import { UrlHelpers } from '../../service/UrlHelpers';
import { DocCenterTooltip, Tooltips } from '../../typings/doc-center';
import { GridRow } from '../../typings/domain';
import { CurrencyCode } from '../../typings/graphql-grid';
import {
    GridCellProps,
    GridColumnDecl,
    GridGroupedColumn,
    GridItemActions,
    GridScreenTotalKey,
    GridSelection,
    GridSourceParams,
    GridTotalStatsInfo,
    RowHeights,
    SortDirection,
    SortLinkUrlGetter,
    SortParams
} from '../../typings/grid';
import { GridHeaderCell } from '../GridHeaderCell/GridHeaderCell';
import { GridScreenTotalCell } from '../GridScreenTotalCell/GridScreenTotalCell';
import { GridTotalBox } from '../GridTotalBox/GridTotalBox';
import { Shape2, Shape2Icon } from '../Shape2/Shape2';
import {
    Cell,
    CellProps,
    Column,
    ColumnGroup,
    Table
} from '../TableDecorator/TableDecorator';
import { WarningMaxLimitTooltip } from '../WarningMaxLimitTooltip/WarningMaxLimitTooltip';

import './Grid.css';
import * as keyset from './Grid.i18n';

const i18n = i18nFactory(keyset);

const gridDefaults = {
    defaultColumnWidth: 100,
    headerHeight: {
        default: 30,
        compact: 66
    },
    selectionColumnWidth: {
        default: 44,
        compact: 44
    },
    rowHeight: {
        default: 65,
        compact: 45
    },
    minWidth: {
        default: 70,
        compact: 30
    }
};

export interface GridProps<T extends GridRow> {
    className?: string;
    rowClassName?: string;
    data: Array<T | null>;
    columns: Array<GridColumnDecl<T>>;
    groupedColumns?: GridGroupedColumn<GridRow>[];
    actions?: GridItemActions;
    sortParams?: SortParams;
    getSortLinkUrl?: SortLinkUrlGetter;
    totalCount: number;
    allItemsIds: Array<string>;
    onGridRowClick?: (index: number) => void;
    selection?: {
        enabled: boolean;
        value?: GridSelection;
        onChange?: (selection: GridSelection) => void;
    };
    rowHeight?: number;
    rowExpanded?: Map<number, boolean>;
    rowsCanExpand?: Map<number, boolean>;
    nextPageToken?: string | null;
    updateColumnsWidth?: (newColumnWidth: number, columnKey: string) => void;
    scrollPosition?: number;
    onHeaderCellMouseEnter?: (tooltipKey: DocCenterTooltip) => void;
    loadTooltipsFromDoccenter?: boolean;
    tooltips?: Tooltips;
    onTableScrollEnd?: (scrollPosition?: number) => void;
    onTableScrollStart?: () => void;
    zebraStriping?: 'default' | 'inverted';
    onTableScroll?: (
        prevScrollPosition: number,
        currentScrollPosition: number
    ) => void;
    isHorizontalScrollVisible?: boolean;
    onTableHeightChange?: (newHeight: number) => void;
    hasGridTotalBox?: boolean;
    isGridTotalBoxNotHiddenByScroll?: boolean;
    totalStats?: GridTotalStatsInfo;
    workCurrency?: CurrencyCode;
    highlightedRowIndex?: number;
    requestIndex: (index: number) => void;
    isGridTotalBoxVisible?: boolean;
    isCompactViewEnabled: boolean;
    // при отображении предупреждения, обязательны параметры (sourceParams и hasObjectsOverLimit)
    hasWarningMaxLimit?: boolean;
    sourceParams?: GridSourceParams;
    hasObjectsOverLimit?: boolean;
}

export interface GridState {
    gridTotalBoxHeight: number;
    isShowTooltipMaxLimit: boolean;
    refCheckboxSelectAll: ReactNode | null;
}

export type GridWithSizeProps<T extends GridRow> = GridProps<T> & {
    width: number;
    height: number;
};

const GRID_DEBOUNCE_TIME = 10;
const HORIZONTAL_FOOTER_SCROLL_BAR_HEIGHT = 17;

interface GridCellComponentProps<T, K> extends CellProps {
    rowData: GridRow | null;
    rowIndex: number;
    actions?: K;
    CellComponent: React.MemoExoticComponent<
        (props: GridCellProps<T, K>) => JSX.Element | null
    >;
}

interface GridCellSkeleton extends CellProps {
    rowData: Array<GridRow> | null;
    rowIndex: number;
    requestIndex: (index: number) => void;
}

interface GridColumnSelector<T> {
    columns: Array<GridColumnDecl<T>>;
    sortParams?: SortParams;
    getSortLinkUrl?: SortLinkUrlGetter;
    onHeaderCellMouseEnter?: (tooltipKey: DocCenterTooltip) => void;
    tooltips?: Tooltips;
    loadTooltipsFromDoccenter?: boolean;
    highlightedRowIndex?: number;
    actions?: GridItemActions;
    isCompactViewEnabled: boolean;
    isGridTotalBoxVisible?: boolean;
    totalStats?: GridTotalStatsInfo;
    isGroupedView?: boolean;
    selection?: {
        enabled: boolean;
        value?: GridSelection;
        onChange?: (selection: GridSelection) => void;
    };
    groupedColumns?: GridGroupedColumn<GridRow>[];
    totalCount?: number;
}

interface GridColumnDataSelector<T> extends GridColumnSelector<T> {
    totalCount: number;
}

export class Grid<T extends GridRow> extends React.PureComponent<
    GridWithSizeProps<T>,
    GridState
> {
    private static GridCellComponent = React.memo(
        ({
            CellComponent,
            rowData,
            rowIndex,
            actions,
            ...props
        }: GridCellComponentProps<GridRow | null, {} | undefined>) => (
            <Cell {...props}>
                <CellComponent
                    rowData={rowData}
                    rowIndex={rowIndex}
                    actions={actions}
                />
            </Cell>
        )
    );

    private static GridCellSkeleton = React.memo(
        ({ rowIndex, rowData, requestIndex, ...props }: GridCellSkeleton) => (
            <Cell {...props}>
                <GridSkeletonContainer
                    cls={Bem.cls('grid', 'skeleton')}
                    height={16}
                />
            </Cell>
        )
    );

    private static GridTable = React.memo(
        (props: TableProps & { children: React.ReactNode }) => (
            <Table {...props} />
        )
    );

    private scrollPosition: number = 0;
    private tableWrapper: RefObject<HTMLDivElement> = React.createRef();
    private unsubscribeScrollPrevention: () => void;
    private fetchGridData = debounce((currentScrollPosition: number) => {
        const {
            isCompactViewEnabled,
            requestIndex,
            rowHeight,
            data,
            height,
            isGridTotalBoxVisible
        } = this.props;

        const currentRowHeight =
            rowHeight ||
            (isCompactViewEnabled
                ? gridDefaults.rowHeight.compact
                : gridDefaults.rowHeight.default);
        const headerHeight =
            isCompactViewEnabled && isGridTotalBoxVisible
                ? gridDefaults.headerHeight.compact
                : gridDefaults.headerHeight.default;

        const index = getEmptyRowDataIndexByCurrentScrollPosition<T>(
            currentScrollPosition,
            data,
            {
                tableHeight: height,
                headerHeight,
                currentRowHeight,
                footerScrollBarHeight: HORIZONTAL_FOOTER_SCROLL_BAR_HEIGHT
            }
        );

        if (index !== undefined) {
            requestIndex(index);
        }
    }, GRID_DEBOUNCE_TIME);

    private getColumnDataWithSelectionColumnSelector = createSelector(
        (data: GridColumnDataSelector<T>) => data.selection,
        (data: GridColumnDataSelector<T>) => data.isGroupedView,
        (data: GridColumnDataSelector<T>) => data.totalCount,
        (
            selection = {
                enabled: true
            },
            isGroupedView,
            totalCount
        ) => {
            const data: Array<React.ReactNode> = [];

            if (!isGroupedView && selection.enabled && selection.value) {
                data.unshift(
                    this.getSelectionColumn(selection.value, totalCount)
                );
            } else if (isGroupedView && selection.enabled && selection.value) {
                data.unshift(
                    this.getGroupedSelectionColumn(selection.value, totalCount)
                );
            }

            return data;
        }
    );

    private getSingleDataColumns = createReSelector(
        (data: GridColumnSelector<T>, index: number) => data.actions,
        (data: GridColumnSelector<T>) => data.columns,
        (data: GridColumnSelector<T>) => data.getSortLinkUrl,
        (data: GridColumnSelector<T>) => data.highlightedRowIndex,
        (data: GridColumnSelector<T>) => data.isCompactViewEnabled,
        (data: GridColumnSelector<T>) => data.isGridTotalBoxVisible,
        (data: GridColumnSelector<T>) => data.loadTooltipsFromDoccenter,
        (data: GridColumnSelector<T>) => data.onHeaderCellMouseEnter,
        (data: GridColumnSelector<T>) => data.sortParams,
        (data: GridColumnSelector<T>) => data.tooltips,
        (data: GridColumnSelector<T>) => data.totalStats,
        (
            actions,
            columnsDecl,
            getSortLinkUrl,
            highlightedRowIndex,
            isCompactViewEnabled,
            isGridTotalBoxVisible,
            loadTooltipsFromDoccenter,
            onHeaderCellMouseEnter,
            sortParams,
            tooltips = {},
            totalStats
        ) => {
            const columns: Array<React.ReactNode> = columnsDecl.map(
                (column: GridColumnDecl<T>, index: number) => {
                    const {
                        key,
                        title,
                        isSortable,
                        CellComponent,
                        defaultSortDirection = SortDirection.desc,
                        align,
                        cellClass,
                        fixed,
                        width,
                        widthCompact,
                        minWidth = isCompactViewEnabled
                            ? gridDefaults.minWidth.compact
                            : gridDefaults.minWidth.default,
                        maxWidth,
                        tooltipKey,
                        flexGrow,
                        isResizable,
                        hintInfo,
                        sortKey,
                        totalKey,
                        isAlignedToLeft,
                        goalId
                    } = column;

                    let { tooltipText } = column;
                    if (!tooltipText) {
                        tooltipText = tooltipKey
                            ? tooltips[tooltipKey]
                            : undefined;
                    }

                    const classNameValue = cellClass
                        ? Bem.cls('grid', cellClass)
                        : '';
                    const testSortKey = sortKey || key;
                    let isSortKey = false;
                    let sortDirection = defaultSortDirection;
                    let nextSortDirection = sortDirection;

                    if (sortParams) {
                        const keyColumnFromSortParams = GridSourceHelper.getColumnKeyBySortParams(
                            sortParams
                        );
                        isSortKey =
                            keyColumnFromSortParams === sortKey ||
                            keyColumnFromSortParams === key;

                        if (isSortKey) {
                            sortDirection = sortParams.direction;
                            nextSortDirection = UrlHelpers.invertSortDirection(
                                sortDirection
                            );
                        }
                    }

                    const gridHeaderCell = (
                        <Cell className={classNameValue}>
                            <GridHeaderCell
                                testSortKey={testSortKey}
                                isSortKey={isSortKey}
                                title={title}
                                isSortable={isSortable !== false}
                                sortDirection={sortDirection}
                                sortLinkUrl={
                                    getSortLinkUrl &&
                                    getSortLinkUrl({
                                        key: sortKey || key,
                                        direction: nextSortDirection,
                                        goalId: goalId
                                    })
                                }
                                onMouseEnter={onHeaderCellMouseEnter}
                                loadTooltipsFromDoccenter={
                                    loadTooltipsFromDoccenter
                                }
                                tooltipKey={tooltipKey}
                                tooltipText={tooltipText}
                                hintInfo={hintInfo}
                                isTitleVisible={true}
                            />
                        </Cell>
                    );

                    const isNameCell = totalKey === GridScreenTotalKey.name;
                    const isCellWithTotal =
                        totalStats && totalKey && totalKey in totalStats;

                    const fractionalPartLength =
                        totalKey !== undefined &&
                        (totalKey === GridScreenTotalKey.cost ||
                            totalKey === GridScreenTotalKey.ctr)
                            ? 2
                            : 0;

                    return (
                        <Column
                            isResizable={
                                isResizable === undefined ? true : isResizable
                            }
                            allowCellsRecycling={true}
                            key={key + index}
                            columnKey={key || sortKey}
                            align={align}
                            fixed={fixed}
                            flexGrow={flexGrow}
                            header={
                                isCompactViewEnabled &&
                                isGridTotalBoxVisible ? (
                                    <div
                                        className={Bem.cls(
                                            'grid',
                                            'compact-header-cell',
                                            {
                                                'with-total-cell':
                                                    isNameCell ||
                                                    isCellWithTotal
                                            }
                                        )}
                                    >
                                        <div
                                            className={Bem.cls(
                                                'grid',
                                                'compact-header-cell-title',
                                                { left: isAlignedToLeft }
                                            )}
                                        >
                                            {gridHeaderCell}
                                        </div>
                                        {(isNameCell || isCellWithTotal) && (
                                            <GridScreenTotalCell
                                                isNameCell={isNameCell}
                                                title={
                                                    totalKey ===
                                                    GridScreenTotalKey.name
                                                        ? i18n('compact-title')
                                                        : totalStats &&
                                                          totalKey &&
                                                          totalStats[totalKey]
                                                }
                                                fractionalPartLength={
                                                    fractionalPartLength
                                                }
                                            />
                                        )}
                                    </div>
                                ) : (
                                    gridHeaderCell
                                )
                            }
                            cell={({ rowIndex = 0, ...props }: CellProps) => {
                                const rowData: T | null = this.getRowData(
                                    rowIndex
                                );

                                if (rowData) {
                                    return (
                                        <Grid.GridCellComponent
                                            {...props}
                                            className={classNames(
                                                classNameValue,
                                                rowIndex === highlightedRowIndex
                                                    ? Bem.cls(
                                                          'grid',
                                                          'highlighted-cell'
                                                      )
                                                    : null,
                                                TestBem.cls('grid', 'cell', {
                                                    id: !!rowData && rowData.id
                                                })
                                            )}
                                            CellComponent={CellComponent}
                                            rowData={rowData}
                                            rowIndex={rowIndex}
                                            actions={actions}
                                            key={rowIndex}
                                        />
                                    );
                                }

                                return (
                                    <Grid.GridCellSkeleton
                                        {...props}
                                        key={rowIndex}
                                        rowData={rowData}
                                        rowIndex={rowIndex}
                                        requestIndex={this.props.requestIndex}
                                        className={classNames(
                                            classNameValue,
                                            rowIndex === highlightedRowIndex
                                                ? Bem.cls(
                                                      'grid',
                                                      'highlighted-cell'
                                                  )
                                                : null,
                                            TestBem.cls('grid', 'cell', {
                                                id:
                                                    !!rowData &&
                                                    (rowData as GridRow).id
                                            })
                                        )}
                                    />
                                );
                            }}
                            minWidth={minWidth}
                            maxWidth={maxWidth}
                            width={
                                isCompactViewEnabled && widthCompact
                                    ? widthCompact
                                    : width
                            }
                        />
                    );
                }
            );

            return columns;
        }
    )((data: GridColumnSelector<T>, index: number) => index);

    private getGridColumnData = createSelector(
        (data: GridColumnDataSelector<T>, index: number) => data.selection,
        (data: GridColumnDataSelector<T>) => data.isGroupedView,
        (data: GridColumnDataSelector<T>) => data.totalCount,
        this.getColumnDataWithSelectionColumnSelector,
        this.getSingleDataColumns,
        (
            selection = {
                enabled: true
            },
            isGroupedView,
            totalCount,
            selectionColumns,
            singleColumns
        ) => {
            return [...selectionColumns, ...singleColumns];
        }
    );

    private getDataGroupedColumns = createSelector(
        (data: GridColumnDataSelector<T>, index: number) => data.actions,
        (data: GridColumnDataSelector<T>) => data.groupedColumns,
        (data: GridColumnDataSelector<T>) => data.getSortLinkUrl,
        (data: GridColumnDataSelector<T>) => data.highlightedRowIndex,
        (data: GridColumnDataSelector<T>) => data.isCompactViewEnabled,
        (data: GridColumnDataSelector<T>) => data.isGridTotalBoxVisible,
        (data: GridColumnDataSelector<T>) => data.loadTooltipsFromDoccenter,
        (data: GridColumnDataSelector<T>) => data.onHeaderCellMouseEnter,
        (data: GridColumnDataSelector<T>) => data.sortParams,
        (data: GridColumnDataSelector<T>) => data.tooltips,
        (data: GridColumnDataSelector<T>) => data.totalStats,
        this.getColumnDataWithSelectionColumnSelector,
        (
            actions,
            columnsDecl,
            getSortLinkUrl,
            highlightedRowIndex,
            isCompactViewEnabled,
            isGridTotalBoxVisible,
            loadTooltipsFromDoccenter,
            onHeaderCellMouseEnter,
            sortParams,
            tooltips = {},
            totalStats,
            selectionColumns
        ) => {
            const columns = columnsDecl
                ? columnsDecl.map((groupedColumn, index) => {
                      return (
                          <ColumnGroup
                              key={groupedColumn.key}
                              header={
                                  <Cell
                                      key={groupedColumn.key}
                                      className={Bem.cls(
                                          'grid',
                                          'grouped-columns-header'
                                      )}
                                  >
                                      {this.getGroupedHeaderCell(groupedColumn)}
                                  </Cell>
                              }
                              fixed={Boolean(groupedColumn.fixed)}
                          >
                              {this.getSingleDataColumns(
                                  {
                                      columns: groupedColumn.columns,
                                      actions,
                                      getSortLinkUrl,
                                      highlightedRowIndex,
                                      isCompactViewEnabled,
                                      isGridTotalBoxVisible,
                                      loadTooltipsFromDoccenter,
                                      onHeaderCellMouseEnter,
                                      sortParams,
                                      tooltips,
                                      totalStats
                                  },
                                  index
                              )}
                          </ColumnGroup>
                      );
                  })
                : [];

            return [...selectionColumns, ...columns];
        }
    );

    constructor(props: GridWithSizeProps<T>) {
        super(props);

        this.state = {
            gridTotalBoxHeight: 66,
            isShowTooltipMaxLimit: false,
            refCheckboxSelectAll: null
        };
    }

    componentDidMount() {
        if (this.tableWrapper.current) {
            this.unsubscribeScrollPrevention = preventMacOsNavigationGesture(
                this.tableWrapper.current
            );
        }
    }

    componentDidUpdate() {
        this.fetchGridData(0);
    }

    componentWillUnmount() {
        if (this.unsubscribeScrollPrevention) {
            this.unsubscribeScrollPrevention();
        }
    }

    render() {
        const {
            className,
            columns,
            groupedColumns,
            totalCount,
            selection = {
                enabled: false
            },
            width,
            height,
            isCompactViewEnabled,
            rowHeight,
            scrollPosition,
            isHorizontalScrollVisible = true,
            onTableHeightChange,
            zebraStriping,
            totalStats,
            workCurrency,
            hasGridTotalBox,
            isGridTotalBoxNotHiddenByScroll,
            isGridTotalBoxVisible,
            actions,
            getSortLinkUrl,
            highlightedRowIndex,
            loadTooltipsFromDoccenter,
            onHeaderCellMouseEnter,
            sortParams,
            tooltips
        } = this.props;

        const { gridTotalBoxHeight } = this.state;
        const isGroupedView = groupedColumns && groupedColumns.length > 0;

        const tableColumns =
            isGroupedView && groupedColumns
                ? this.getDataGroupedColumns(
                      {
                          actions,
                          groupedColumns,
                          columns,
                          getSortLinkUrl,
                          highlightedRowIndex,
                          isCompactViewEnabled,
                          isGridTotalBoxVisible,
                          loadTooltipsFromDoccenter,
                          onHeaderCellMouseEnter,
                          sortParams,
                          tooltips,
                          totalStats,
                          selection,
                          isGroupedView,
                          totalCount
                      },
                      0
                  )
                : this.getGridColumnData(
                      {
                          actions,
                          columns,
                          getSortLinkUrl,
                          highlightedRowIndex,
                          isCompactViewEnabled,
                          isGridTotalBoxVisible,
                          loadTooltipsFromDoccenter,
                          onHeaderCellMouseEnter,
                          sortParams,
                          tooltips,
                          totalStats,
                          selection,
                          isGroupedView,
                          totalCount
                      },
                      0
                  );

        const cls = classNames(
            className,
            Bem.cls({
                block: 'grid',
                mods: {
                    'zebra-striping': zebraStriping ? zebraStriping : false,
                    compact: isCompactViewEnabled,
                    'with-total':
                        isGridTotalBoxVisible === true && isCompactViewEnabled
                }
            })
        );

        const totalBoxHeight =
            isGridTotalBoxVisible && !isCompactViewEnabled
                ? gridTotalBoxHeight
                : 0;
        const tableHeight =
            height - (isGridTotalBoxNotHiddenByScroll ? totalBoxHeight : 0);

        const defaultRowHeight = isCompactViewEnabled
            ? gridDefaults.rowHeight.compact
            : gridDefaults.rowHeight.default;

        return (
            <div
                ref={this.tableWrapper}
                className={cls}
                style={{
                    width: `${width}px`,
                    height: `${height - totalBoxHeight}px`,
                    transform: `translateZ(0) translateY(-${
                        isGridTotalBoxNotHiddenByScroll ? 0 : totalBoxHeight
                    }px)`
                }}
            >
                {hasGridTotalBox &&
                isGridTotalBoxVisible &&
                !isCompactViewEnabled ? (
                    <div>
                        <GridTotalBox
                            totalStats={totalStats}
                            currency={workCurrency}
                        />
                    </div>
                ) : null}

                {this.renderTooltipMaxLimit()}

                <Grid.GridTable
                    onColumnResizeEndCallback={this.onColumnResizeEndCallback}
                    isColumnResizing={false}
                    showScrollbarX={isHorizontalScrollVisible}
                    onVerticalScroll={this.onVerticalScroll}
                    onScrollStart={this.onScrollStart}
                    width={width}
                    height={tableHeight}
                    className={Bem.cls('grid-fixed-data-table')}
                    onContentHeightChange={onTableHeightChange}
                    headerHeight={
                        isCompactViewEnabled && isGridTotalBoxVisible
                            ? gridDefaults.headerHeight.compact
                            : gridDefaults.headerHeight.default
                    }
                    rowHeight={rowHeight ? rowHeight : defaultRowHeight}
                    scrollToRow={scrollPosition}
                    rowsCount={totalCount}
                    rowHeightGetter={this.getRowHeight}
                    bufferRowCount={0}
                    onScrollEnd={this.onScrollEnd}
                    rowClassNameGetter={this.rowClassNameGetter}
                    onRowClick={this.onRowClick}
                    groupHeaderHeight={30}
                    keyboardScrollEnabled={true}
                >
                    {tableColumns}
                </Grid.GridTable>
            </div>
        );
    }

    private rowClassNameGetter = () => {
        const { zebraStriping, rowClassName } = this.props;

        const rowAdditionalClass = classNames(
            zebraStriping
                ? `public_fixedDataTableRow_zebra-striping-${zebraStriping}`
                : 'public_fixedDataTableRow',
            rowClassName
        );

        return rowAdditionalClass;
    };

    private onScrollStart = () => {
        const { onTableScrollStart } = this.props;

        if (onTableScrollStart) {
            onTableScrollStart();
        }
    };

    private onTableScrollEnd = (scrollPosition: number) => {
        const { onTableScrollEnd } = this.props;

        if (onTableScrollEnd) {
            onTableScrollEnd(scrollPosition);
        }
    };

    private onTableScroll = (
        prevScrollPosition: number,
        currentScrollPosition: number
    ) => {
        const { onTableScroll } = this.props;

        if (onTableScroll) {
            onTableScroll(prevScrollPosition, currentScrollPosition);
        }
    };

    private onScrollEnd = (x: number, y: number) => {
        const { scrollPosition, props } = this;

        if (props.scrollPosition === 0) {
            this.onTableScroll(scrollPosition, y);
            this.scrollPosition = y;
        }

        this.onTableScrollEnd(y);
    };

    private onRowClick = (
        event: React.SyntheticEvent<Table>,
        rowIndex: number
    ) => {
        const { onGridRowClick } = this.props;

        if (onGridRowClick) {
            onGridRowClick(rowIndex);
        }
    };

    private onVerticalScroll = (currentScrollPosition: number) => {
        this.onTableScroll(this.scrollPosition, currentScrollPosition);
        this.scrollPosition = currentScrollPosition;
        this.fetchGridData(currentScrollPosition);

        return true;
    };

    private onColumnResizeEndCallback = (
        newColumnWidth: number,
        columnKey: string
    ) => {
        const { updateColumnsWidth } = this.props;

        if (updateColumnsWidth) {
            updateColumnsWidth(newColumnWidth, columnKey);
        }
    };

    /**
     * Обработчик события клика по чекбоксу одной из строк
     */
    private onGridRowSelectChange = (id: string) => {
        const {
            selection = {
                enabled: false
            },
            totalCount,
            hasObjectsOverLimit,
            hasWarningMaxLimit
        } = this.props;

        if (!selection.onChange || !selection.value) {
            return;
        }

        const selectionValue = [...selection.value];
        const indexInSelectionItems = selectionValue.indexOf(id);

        if (indexInSelectionItems === -1) {
            selectionValue.push(id);
        } else {
            selectionValue.splice(indexInSelectionItems, 1);
        }

        selection.onChange(selectionValue);

        const isSelectAllElements = selectionValue.length === totalCount;

        if (hasWarningMaxLimit && hasObjectsOverLimit) {
            this.setState({
                isShowTooltipMaxLimit: isSelectAllElements
            });
        }
    };

    /**
     * Обработчик события клика по чекбоксу массового выбора строк
     */
    private onAllGridRowsSelectionChange = () => {
        const {
            selection = {
                enabled: false
            },
            allItemsIds,
            totalCount,
            hasObjectsOverLimit,
            hasWarningMaxLimit
        } = this.props;

        const selectionValue = selection.value;

        if (!selection.onChange || !selectionValue) {
            return;
        }

        const wasChecked =
            selectionValue.length !== 0 && selectionValue.length === totalCount;

        selection.onChange(wasChecked ? [] : [...allItemsIds]);

        if (hasWarningMaxLimit && hasObjectsOverLimit) {
            this.setState({
                isShowTooltipMaxLimit: !wasChecked
            });
        }
    };

    private renderTooltipMaxLimit() {
        const { isShowTooltipMaxLimit, refCheckboxSelectAll } = this.state;
        const { hasWarningMaxLimit, totalCount, sourceParams } = this.props;

        if (!hasWarningMaxLimit || !sourceParams || !totalCount) {
            return null;
        }

        return (
            <WarningMaxLimitTooltip
                visible={isShowTooltipMaxLimit}
                sourceParamsView={sourceParams.view}
                totalCount={totalCount}
                refParent={refCheckboxSelectAll}
                onOutsideClick={this.onOutsideClickTooltipMaxLimit}
            />
        );
    }

    private getGroupedHeaderCell = (groupedColumn: GridGroupedColumn<T>) => {
        if (groupedColumn.title) {
            return (
                <GridHeaderCell
                    isSortable={false}
                    isSortKey={false}
                    title={groupedColumn.title || ''}
                    isTitleVisible={true}
                    leftIcon={this.renderIconGoal(groupedColumn.isGoalIcon)}
                />
            );
        }
        return undefined;
    };

    private renderIconGoal = (isGoalIcon?: boolean) => {
        if (!isGoalIcon) {
            return undefined;
        }

        return (
            <Shape2
                cls={Bem.cls('grid', 'icon-goal')}
                icon={Shape2Icon.aiming}
                width={12}
                height={12}
            />
        );
    };
    /**
     * Возвращает массив колонок для таблицы
     */

    private getGroupedSelectionColumn(
        selectionValue: GridSelection,
        totalCount: number
    ) {
        return (
            <ColumnGroup key={'grouped-selection'} fixed={true}>
                {this.getSelectionColumn(selectionValue, totalCount)}
            </ColumnGroup>
        );
    }

    /**
     * Возвращает колонку с чекбоксом выбора строки таблицы
     */
    private getSelectionColumn(
        selectionValue: GridSelection,
        totalCount: number
    ) {
        const { isCompactViewEnabled, isGridTotalBoxVisible } = this.props;

        const checkBox = (
            <CheckBox
                size="s"
                view="default"
                theme="normal"
                tone="default"
                checked={
                    selectionValue.length !== 0 &&
                    selectionValue.length === totalCount
                }
                onChange={this.onAllGridRowsSelectionChange}
                ref={ref => this.setRefWarningMaxLimitTooltip(ref)}
            />
        );

        return (
            <Column
                columnKey="selection-column"
                key="selection-column"
                fixed={true}
                header={
                    isCompactViewEnabled && isGridTotalBoxVisible ? (
                        <div className={Bem.cls('grid', 'compact-header-cell')}>
                            <div
                                className={Bem.cls(
                                    'grid',
                                    'compact-header-cell-title',
                                    { left: true }
                                )}
                            >
                                <Cell>{checkBox}</Cell>
                            </div>
                        </div>
                    ) : (
                        <Cell>{checkBox}</Cell>
                    )
                }
                cell={({ rowIndex = 0, ...props }: CellProps) => {
                    const rowData = this.getRowData(rowIndex);
                    let cellContent;

                    if (rowData) {
                        cellContent = (
                            <div
                                className={classNames(
                                    Bem.cls('select-row-cell'),
                                    TestBem.cls({
                                        block: 'select-row-cell',
                                        mods: {
                                            id: rowData.id
                                        }
                                    })
                                )}
                            >
                                <CheckBox
                                    size="s"
                                    view="default"
                                    theme="normal"
                                    tone="default"
                                    checked={
                                        selectionValue.indexOf(rowData.id) !==
                                        -1
                                    }
                                    onChange={() =>
                                        this.onGridRowSelectChange(rowData.id)
                                    }
                                />
                            </div>
                        );
                    } else {
                        cellContent = <GridSkeletonContainer height={16} />;
                    }

                    return <Cell {...props}>{cellContent}</Cell>;
                }}
                width={
                    isCompactViewEnabled
                        ? gridDefaults.selectionColumnWidth.compact
                        : gridDefaults.selectionColumnWidth.default
                }
            />
        );
    }

    private setRefWarningMaxLimitTooltip(ref: ReactNode) {
        this.setState({ refCheckboxSelectAll: ref });
    }

    private onOutsideClickTooltipMaxLimit = () => {
        this.setState({ isShowTooltipMaxLimit: false });
    };

    /**
     * Возвращает данные для формирования строки таблицы для соответствующего индекса
     */
    private getRowData(index: number) {
        const { data } = this.props;

        if (data[index]) {
            return data[index];
        }

        return null;
    }

    private getRowHeight = (index: number): number => {
        const {
            rowExpanded,
            rowsCanExpand,
            isCompactViewEnabled,
            rowHeight
        } = this.props;

        const defaultRowHeight = isCompactViewEnabled
            ? gridDefaults.rowHeight.compact
            : gridDefaults.rowHeight.default;
        const innerRowHeight = rowHeight ? rowHeight : defaultRowHeight;

        const isRowExpanded = rowExpanded
                ? Boolean(rowExpanded.get(index))
                : false,
            rowCanExpand = rowsCanExpand
                ? Boolean(rowsCanExpand.get(index))
                : false,
            calculatedRowHeight = maxHeightHelper
                ? maxHeightHelper.getRowHeight(
                      index,
                      isRowExpanded,
                      rowCanExpand
                  )
                : RowHeights.default;

        return rowExpanded ? calculatedRowHeight : innerRowHeight;
    };
}
